'use client';

import { getImageDimensions } from '@/storyblok/utils/asset';
import { PostStoryblok, RichtextStoryblok, StoryblokComponent } from '@/types/types-storyblok';
import { Lora } from 'next/font/google';
import { StoryblokStory } from 'storyblok-generate-ts';
import Image from 'next/image';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { MARK_BOLD, render } from 'storyblok-rich-text-react-renderer';
import { PostCard } from './latestPosts/postCard';
import { makeRichTextRenderOptions } from '@/storyblok/utils/richText';

const lora = Lora({ subsets: ['latin'], weight: '500', style: 'italic' });

const SLIDE_DURATION = 10000;

export const LatestPosts = ({
  story,
  posts,
  title,
}: {
  story: StoryblokStory<StoryblokComponent>;
  title: RichtextStoryblok;
  posts: StoryblokStory<PostStoryblok>[];
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(slide => (slide + 1 > posts.length - 1 ? 0 : slide + 1));
    }, SLIDE_DURATION);
    return () => clearInterval(interval);
  }, [posts.length, currentSlide]);

  if (posts.length === 0) return null;

  return (
    <section className="mx-2 px:mx-10 xl:max-w-screen-xl xl:mx-auto pt-12 lg:p-0 pb-8 bg-brandBlue-50 shadow-1 rounded-2xl overflow-hidden">
      <div className="flex flex-col gap-8 lg:grid lg:grid-cols-2 lg:p-14 lg:gap-x-44 xl:grid-cols-[1fr,1.5fr]">
        <h2 className="text-3xl lg:text-5xl text-center px-2 lg:px-0 lg:text-left lg:col-start-1 lg:leading-tight">
          {render(
            title,
            makeRichTextRenderOptions(story, {
              markResolvers: {
                [MARK_BOLD]: children => <span className={clsx(lora.className, 'text-elty-green')}>{children}</span>,
              },
            }),
          )}
        </h2>
        <ul className="flex flex-row relative lg:col-start-1 overflow-x-hidden">
          {posts.map((post, i) => (
            <PostCard post={post} current={i === currentSlide} index={i} key={post.id} />
          ))}
        </ul>
        <hr className="mx-4 lg:col-start-1" />
        <div className="flex flex-row items-center justify-center lg:justify-start px-14 lg:px-0 gap-2 lg:col-start-1">
          {posts.map((post, i) => (
            <button
              aria-label={`mostra l'articolo numero ${i}`}
              key={post.id}
              onClick={() => setCurrentSlide(i)}
              className={clsx(
                'rounded-full transition-colors duration-300 max-w-9 w-1/3 h-2',
                i === currentSlide ? 'bg-elty-green' : 'bg-background-dark',
              )}
            />
          ))}
        </div>
        {posts.map((post, i) =>
          post.content.image?.filename ? (
            <Image
              key={post.id}
              className={clsx(
                'transition-opacity w-full aspect-[1.24] xl:aspect-[1.3] object-cover rounded-xl bg-gray-300 hidden lg:inline-block col-start-2 row-start-1 row-span-4 relative',
                i !== currentSlide ? 'opacity-0' : 'z-10',
              )}
              src={post.content.image?.filename}
              alt={post.content.image?.alt ?? 'immagine guida'}
              {...getImageDimensions(post.content.image)}
            />
          ) : (
            <div
              key={post.id}
              className={clsx(
                'transition-opacity w-full aspect-[1.24] xl:aspect-[1.7] bg-gray-300 rounded-xl hidden lg:inline-block col-start-2 row-start-1 row-span-4 relative',
                i !== currentSlide ? 'opacity-0' : 'z-10',
              )}
            />
          ),
        )}
      </div>
    </section>
  );
};
